import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card, Table, Button, Typography, Select, Space, message,
    Modal, Form, Row, Col, Input, Divider
} from 'antd';
import { ReloadOutlined, EnvironmentOutlined, EditOutlined } from '@ant-design/icons';
import apiClient from '../helpers/apiHelper';
import { ENDPOINTS, REQUEST_TYPES, MESSAGES } from '../helpers/apiConfig';
import { buildRouteMapsUrl } from '../helpers/mapsHelper';
import { ROLES } from '../constants';

const { Title } = Typography;
const { TextArea } = Input;

const PLANT_USER_ROLE = ROLES.PLANT_USER;
const OWNER_ROLE = ROLES.OWNER;

const toTimeOnly = (value) => {
    const str = (value || '').trim();
    if (!str) {
        return '';
    }
    const parts = str.split(' ');
    return parts.length > 1 ? parts[parts.length - 1] : str;
};

export const LogEntryPage = ({ user }) => {
    const [logEntries, setLogEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [routeLoading, setRouteLoading] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [editSubmitLoading, setEditSubmitLoading] = useState(false);
    const [editForm] = Form.useForm();

    const userRole = user?.role;
    const canEditUserRemarks = userRole === PLANT_USER_ROLE;
    const canEditOwnerRemarks = userRole === OWNER_ROLE;
    const canEdit = canEditUserRemarks || canEditOwnerRemarks;

    const openEditModal = useCallback((record) => {
        setEditRecord(record);
        editForm.setFieldsValue({
            date: record.date,
            vehicle_no: record.vehicle_no,
            start_time: toTimeOnly(record.start_time),
            end_time: toTimeOnly(record.end_time),
            gps_total_km: record.gps_total_km,
            user_remarks: record.user_remarks ?? '',
            owner_remarks: record.owner_remarks ?? '',
        });
        setEditModalOpen(true);
    }, [editForm]);

    const closeEditModal = useCallback(() => {
        setEditModalOpen(false);
        setEditRecord(null);
        editForm.resetFields();
    }, [editForm]);

    const handleEditSubmit = useCallback(async (values) => {
        if (!editRecord) {
            return;
        }
        setEditSubmitLoading(true);
        try {
            const payload = {
                request_type: REQUEST_TYPES.UPDATE_LOG_ENTRY,
                email: user?.email,
                vehicle_no: editRecord.vehicle_no,
                gps_imei_no: editRecord.gps_imei_no,
                date: editRecord.date,
            };
            if (canEditUserRemarks) {
                payload.user_remarks = values.user_remarks ?? '';
            } else if (canEditOwnerRemarks) {
                payload.owner_remarks = values.owner_remarks ?? '';
            }

            await apiClient(ENDPOINTS.LOG_ENTRY.UPDATE_LOG_ENTRY, {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            message.success(MESSAGES.SUCCESS.UPDATE_LOG_ENTRY);
            closeEditModal();
            fetchLogEntries();
        } catch (error) {
            message.error(error?.message || MESSAGES.ERROR.UPDATE_LOG_ENTRY);
        } finally {
            setEditSubmitLoading(false);
        }
    }, [editRecord, user?.email, canEditUserRemarks, canEditOwnerRemarks, closeEditModal]);

    const handleViewRoute = useCallback(async (record) => {
        const vehicleNo = (record?.vehicle_no || '').trim();
        const date = (record?.date || '').trim();
        if (!vehicleNo || !date) {
            message.error(MESSAGES.ERROR.VEHICLE_ROUTE_NO_DATA);
            return;
        }

        const rowKey = `${vehicleNo}|${date}`;

        const mapsTab = window.open('', '_blank');
        if (mapsTab) {
            mapsTab.opener = null;
        }
        setRouteLoading((prev) => ({ ...prev, [rowKey]: true }));

        try {
            const res = await apiClient(ENDPOINTS.LOG_ENTRY.FETCH_VEHICLE_ROUTE, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_VEHICLE_ROUTE,
                    vehicle_no: vehicleNo,
                    date,
                }),
            });

            const mapsUrl = buildRouteMapsUrl(res?.points);
            if (!mapsUrl) {
                if (mapsTab) {
                    mapsTab.close();
                }
                message.error(MESSAGES.ERROR.FETCH_VEHICLE_ROUTE);
                return;
            }

            if (mapsTab) {
                mapsTab.location.href = mapsUrl;
            } else {
                window.location.href = mapsUrl;
            }
            message.success(MESSAGES.SUCCESS.FETCH_VEHICLE_ROUTE);
        } catch (error) {
            if (mapsTab) {
                mapsTab.close();
            }
            message.error(error?.message || MESSAGES.ERROR.FETCH_VEHICLE_ROUTE);
        } finally {
            setRouteLoading((prev) => ({ ...prev, [rowKey]: false }));
        }
    }, []);

    const fetchLogEntries = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient(ENDPOINTS.LOG_ENTRY.FETCH_LOG_ENTRIES, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_LOG_ENTRIES,
                    email: user?.email,
                }),
            });

            setLogEntries(res?.items || []);
        } catch (error) {
            message.error(error?.message || MESSAGES.ERROR.FETCH_LOG_ENTRIES);
            setLogEntries([]);
        } finally {
            setLoading(false);
        }
    }, [user?.email]);

    useEffect(() => {
        fetchLogEntries();
    }, [fetchLogEntries]);

    const flattenedEntries = useMemo(() => {
        const rows = [];
        for (const item of logEntries) {
            if (Array.isArray(item.daily_km)) {
                for (const day of item.daily_km) {
                    rows.push({
                        vehicle_no: item.vehicle_no,
                        gps_imei_no: item.gps_imei_no,
                        vehicle_information: item.vehicle_information,
                        company: item.company,
                        branch: item.branch,
                        date: day.date,
                        start_time: day.start_time,
                        end_time: day.end_time,
                        gps_total_km: day.gps_total_km,
                        user_remarks: day.user_remarks ?? item.user_remarks ?? '',
                        owner_remarks: day.owner_remarks ?? item.owner_remarks ?? '',
                    });
                }
            } else {
                rows.push({
                    ...item,
                    date: item.date ?? item.log_date ?? '',
                    gps_total_km: item.gps_total_km ?? item.running_km,
                });
            }
        }
        const toSortKey = (value) => {
            const [dd = '', mm = '', yyyy = ''] = (value || '').split('-');
            return `${yyyy}${mm}${dd}`;
        };
        rows.sort((a, b) =>
            toSortKey(b.date).localeCompare(toSortKey(a.date)) ||
            (a.vehicle_no || '').localeCompare(b.vehicle_no || '')
        );
        return rows;
    }, [logEntries]);

    const vehicleOptions = useMemo(() => {
        const seen = new Set();
        for (const row of flattenedEntries) {
            const v = (row.vehicle_no || '').trim();
            if (v) {
                seen.add(v);
            }
        }
        return Array.from(seen)
            .sort((a, b) => a.localeCompare(b))
            .map((v) => ({ label: v, value: v }));
    }, [flattenedEntries]);

    const filteredEntries = useMemo(() => {
        if (!selectedVehicle) {
            return flattenedEntries;
        }
        return flattenedEntries.filter((row) => row.vehicle_no === selectedVehicle);
    }, [flattenedEntries, selectedVehicle]);

    const columns = useMemo(
        () => [
            { title: 'Date', dataIndex: 'date', key: 'date', width: 120 },
            { title: 'Vehicle No', dataIndex: 'vehicle_no', key: 'vehicle_no', width: 130 },
            {
                title: 'Start Time',
                dataIndex: 'start_time',
                key: 'start_time',
                width: 110,
                render: (value) => toTimeOnly(value),
            },
            {
                title: 'End Time',
                dataIndex: 'end_time',
                key: 'end_time',
                width: 110,
                render: (value) => toTimeOnly(value),
            },
            {
                title: 'Total Hours',
                dataIndex: 'total_hours',
                key: 'total_hours',
                width: 110,
            },
            {
                title: 'Running Km',
                dataIndex: 'gps_total_km',
                key: 'gps_total_km',
                width: 130,
                render: (value) =>
                    value === null || value === undefined || value === ''
                        ? value
                        : `${value} kms`,
            },
            {
                title: 'GPS Route for the Day',
                dataIndex: 'gps_route',
                key: 'gps_route',
                width: 180,
                render: (_, record) => {
                    const rowKey = `${record.vehicle_no}|${record.date}`;
                    const runningKm = Number(record.gps_total_km);
                    const noDistance = Number.isFinite(runningKm) && runningKm === 0;
                    return (
                        <Button
                            type="link"
                            icon={<EnvironmentOutlined />}
                            onClick={() => handleViewRoute(record)}
                            loading={!!routeLoading[rowKey]}
                            disabled={noDistance}
                            size="small"
                        >
                            View Route
                        </Button>
                    );
                },
            },
            { title: 'User Remarks', dataIndex: 'user_remarks', key: 'user_remarks', width: 200 },
            { title: 'Owner Remarks', dataIndex: 'owner_remarks', key: 'owner_remarks', width: 200 },
            {
                title: 'Action',
                key: 'action',
                fixed: 'right',
                width: 100,
                render: (_, record) => (
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(record)}
                        disabled={!canEdit}
                        size="small"
                    >
                        Edit
                    </Button>
                ),
            },
        ],
        [handleViewRoute, routeLoading, openEditModal, canEdit]
    );

    return (
        <div>
            <Card
                title={<Title level={4} style={{ marginBottom: 0 }}>Log Entry</Title>}
                extra={
                    <Space>
                        <Select
                            allowClear
                            showSearch
                            placeholder="Filter by Vehicle No"
                            value={selectedVehicle || undefined}
                            onChange={(value) => setSelectedVehicle(value || '')}
                            options={vehicleOptions}
                            optionFilterProp="label"
                            style={{ width: 220 }}
                        />
                        <Button
                            onClick={fetchLogEntries}
                            loading={loading}
                            icon={<ReloadOutlined />}
                        >
                            Refresh
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={filteredEntries}
                    rowKey={(record, index) => `${record.gps_imei_no || record.vehicle_no}_${record.date}_${index}`}
                    loading={loading}
                    pagination={filteredEntries.length > 10}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: 'No log entries found' }}
                />
            </Card>

            <Modal
                title={<Title level={4} style={{ marginBottom: 0 }}>Edit Log Entry</Title>}
                open={editModalOpen}
                onCancel={closeEditModal}
                footer={null}
                width={720}
                destroyOnClose
            >
                <Divider style={{ marginTop: 0 }} />
                <Form
                    form={editForm}
                    layout="vertical"
                    className="bold-labels"
                    onFinish={handleEditSubmit}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Date" name="date">
                                <Input disabled />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Vehicle No." name="vehicle_no">
                                <Input disabled />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Start Time" name="start_time">
                                <Input disabled />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="End Time" name="end_time">
                                <Input disabled />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item label="Running Km" name="gps_total_km">
                                <Input disabled />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="User Remarks" name="user_remarks">
                                <TextArea
                                    rows={3}
                                    placeholder="Enter user remarks"
                                    disabled={!canEditUserRemarks}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Owner Remarks" name="owner_remarks">
                                <TextArea
                                    rows={3}
                                    placeholder="Enter owner remarks"
                                    disabled={!canEditOwnerRemarks}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row justify="end" gutter={16} className="mt-2">
                        <Col>
                            <Button onClick={closeEditModal}>Cancel</Button>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                htmlType="submit"
                                danger
                                loading={editSubmitLoading}
                            >
                                Update
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    );
};
